import { useEffect, useState } from "react";
import { settingsApi } from "@/lib/api";

// Global in-memory cache — one HTTP call for all consumers.
let cachedImages = null;
let inflight = null;
const subscribers = new Set();

const notify = () => {
  subscribers.forEach((cb) => cb(cachedImages));
};

const fetchOnce = async () => {
  if (cachedImages !== null) return cachedImages;
  if (inflight) return inflight;
  inflight = settingsApi
    .get()
    .then((s) => {
      cachedImages = (s && s.images) || {};
      notify();
      return cachedImages;
    })
    .catch(() => {
      cachedImages = {};
      notify();
      return cachedImages;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

// Public helper — invalidate cache after admin uploads/deletes.
export const refreshSiteImages = () => {
  cachedImages = null;
  return fetchOnce();
};

/**
 * Returns the URL of the given site-image slot, or `fallback` when the
 * admin hasn't uploaded a custom one. Absolute paths coming from the
 * backend (`/api/gallery-files/...`) are rewritten to include the backend
 * host so the frontend proxy can resolve them.
 */
export const useSiteImage = (slot, fallback = "") => {
  const [images, setImages] = useState(cachedImages || {});
  useEffect(() => {
    const cb = (v) => setImages(v || {});
    subscribers.add(cb);
    fetchOnce();
    return () => {
      subscribers.delete(cb);
    };
  }, []);
  const raw = images?.[slot];
  if (!raw) return fallback;
  if (raw.startsWith("/api/")) {
    const host = process.env.REACT_APP_BACKEND_URL || "";
    return `${host}${raw}`;
  }
  return raw;
};
